import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFullScreenLoaderEffect } from '@/shared/ui';
import { captureError } from '@/shared/utils';

type AuthContextType = {
  accessToken?: string;
  setAccessToken: (token: string) => void;
  refetchAccessToken: () => void;
};

export const AuthContext = createContext({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [accessToken, setAccessToken] = useState<string>();
  useFullScreenLoaderEffect(isLoading);

  const refetchAccessToken = useCallback(async () => {
    getAccessTokenSilently({ cacheMode: 'off' }).then((token) => {
      setAccessToken(token);
      window.location.reload();
    });
  }, []);

  useEffect(() => {
    try {
      if (isAuthenticated) {
        getAccessTokenSilently().then(setAccessToken);
      } else {
        setAccessToken(undefined);
      }
    } catch (error) {
      captureError(`Error while retreiving token: ${error}`);
    }
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{ accessToken, setAccessToken, refetchAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within PrivateRoutes');
  }
  return context;
};
